// Shared date formatters (s150). Two value types, two timezone rules — both deliberate:
//
//  • DATE-ONLY strings ("YYYY-MM-DD", e.g. death_events.date_of_death) carry NO time or zone.
//    `new Date("2026-05-01")` parses as UTC midnight; formatting WITHOUT a timeZone then renders in
//    the viewer's/runner's local zone and shifts the calendar day (ET showed "April 30" for a
//    "2026-05-01" value — the s150 dashboard bug). Pin to UTC so the calendar day never moves.
//
//  • TIMESTAMPS (e.g. estate_snapshots.generated_at) ARE an instant; render them in the app's
//    canonical wall-clock zone (America/Toronto).
//
// 🔴 Bug class: `new Date(dateOnly)` + local-zone format is an off-by-one anywhere it appears.
// Prefer formatDateOnlyLong for any date-only value.

/** Long-form calendar date for a DATE-ONLY string ("2026-05-01" → "May 1, 2026"), timezone-safe
 *  (pinned to UTC so the day never shifts by local offset). Empty/invalid input passes through. */
export function formatDateOnlyLong(dateOnly: string | null | undefined): string {
  if (!dateOnly) return "";
  const d = new Date(dateOnly);
  if (Number.isNaN(d.getTime())) return dateOnly;
  return d.toLocaleDateString("en-US", { timeZone: "UTC", month: "long", day: "numeric", year: "numeric" });
}

/** Parse a DATE-ONLY string ("2026-05-01") to a Date at LOCAL midnight, so date-fns math
 *  (addDays/isBefore) and any local formatting stay on the intended calendar day — `new Date(dateOnly)`
 *  yields UTC midnight, which reads back a day early on negative-offset viewers (the s150 off-by-one).
 *  Use for date-only values that FEED date arithmetic; use formatDateOnlyLong for display-only.
 *  Empty/invalid → null. */
export function parseDateOnlyLocal(dateOnly: string | null | undefined): Date | null {
  if (!dateOnly) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateOnly);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Long-form calendar date for a TIMESTAMP, in the app's canonical zone (America/Toronto).
 *  ("2026-07-19T…Z" → "July 19, 2026"). Null/invalid → "". */
export function formatTimestampEtLong(ts: Date | string | null | undefined): string {
  if (!ts) return "";
  const d = ts instanceof Date ? ts : new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { timeZone: "America/Toronto", month: "long", day: "numeric", year: "numeric" });
}
